import clsx from "clsx";
import React, {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from "react";
import CreatableSelect from "react-select/creatable";
import Select, { components } from "react-select";
import FieldLabel from "../FieldLabel/FieldLabel";
import Badge, { EBadgeKind } from "../Badge/Badge";
import { MdAdd, MdOutlineManageSearch } from "react-icons/md";
import useMobile from "src/hooks/useMobile";
import SelectModal from "../Modals/SelectModal";
import Button from "../Button/Button";
import { useTranslation } from "react-i18next";
import Icon from "../Icon/Icon";
import { ICON_SIZES } from "src/constants/icons";
import CurrencyFlag from "react-currency-flags";
import ReactCountryFlag from "react-country-flag";

export enum ECustomSelectIconType {
  ICON = "icon",
  CURRENCY = "currency",
  COUNTRY = "country",
}

export interface ICustomSelectOption {
  value: string;
  label: string;
  icon?: any;
  iconStyles?: any;
  iconType?: ECustomSelectIconType;
}

export interface ICustomSelectOptionGroup {
  label: string;
  options: ICustomSelectOption[];
}

export type TCustomSelectOptions =
  | ICustomSelectOption[]
  | ICustomSelectOptionGroup[];

interface ICustomSelect {
  options: TCustomSelectOptions;
  placeholder?: string;
  label?: string;
  id?: string;
  required?: boolean;
  value?:
    | string
    | string[]
    | ICustomSelectOption
    | ICustomSelectOption[]
    | null;
  onChange: (e: any) => void;
  disabled?: boolean;
  autoFocus?: boolean;
  form?: string;
  creatable?: boolean;
  useValueAsLabel?: boolean;
  isClearable?: boolean;
  isSearchable?: boolean;
  isMulti?: boolean;
  hideSelectedOptions?: boolean;
  closeMenuOnSelect?: boolean;
  allowSearchModal?: boolean;
  isInsideGrid?: boolean;
  helpText?: string;
  withIcons?: boolean;
  searchModalOptions?: {
    title?: string | undefined;
    description?: string;
  } | null;
}

const isOptionGroupArray = (
  options: TCustomSelectOptions,
): options is ICustomSelectOptionGroup[] => {
  return Array.isArray(options) && !!options.length && "options" in options[0];
};

const flattenOptions = (
  options: TCustomSelectOptions,
): ICustomSelectOption[] => {
  if (!Array.isArray(options) || !options.length) return [];

  if (isOptionGroupArray(options)) {
    return options.flatMap((group) => group.options || []);
  }

  return options;
};

const isOption = (item: any): item is ICustomSelectOption => {
  return (
    !!item &&
    typeof item === "object" &&
    !Array.isArray(item) &&
    "value" in item &&
    "label" in item
  );
};

const isOptionArray = (items: any): items is ICustomSelectOption[] => {
  return Array.isArray(items) && items.every(isOption);
};

const CustomValueContainer = (props: any) => {
  const { isMulti } = props.selectProps;
  const { t } = useTranslation(["inputs"]);

  if (!isMulti) return <components.ValueContainer {...props} />;

  const selected = props.getValue();
  const children = React.Children.toArray(props?.children || []);
  const input = children[children.length - 1];

  const classNames = clsx(
    "dark:!bg-cyan-400",
    "!bg-gray-400",
    "!text-white",
    "dark:!text-black",
  );

  const badge = (
    <Badge
      text={t("inputs:select.selected_count", {
        count: selected.length,
      })}
      className={classNames}
    />
  );

  return (
    <components.ValueContainer {...props}>
      {selected.length > 0 ? badge : children.slice(0, -1)}
      {input}
    </components.ValueContainer>
  );
};

const CustomMenuList = (props: any) => {
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    requestAnimationFrame(() => {
      if (props.children?.length) {
        const selectedIndex = props.children.findIndex(
          (child: any) => child?.props?.isSelected,
        );

        const menuEl = menuRef.current;
        const selectedEl = menuEl?.children[selectedIndex];

        if (selectedIndex >= 0 && selectedEl) {
          selectedEl.scrollIntoView({
            block: "center",
          });
        }
      }
    });
  }, []);

  return <components.MenuList {...props} innerRef={menuRef} />;
};

const sharedStyles = {
  valueContainer: (base: any) => ({
    ...base,
    flexWrap: "nowrap",
    overflowX: "auto",
  }),
  menuPortal: (base: any) => ({ ...base, zIndex: 9999, fontSize: 14 }),
  control: (styles: any) => ({
    ...styles,
    borderRadius: 0,
    ":focus": { outline: "0" },
  }),
};

const sharedClassNames = {
  control: () =>
    clsx(
      "!bg-transparent",
      "!text-blue-500",
      "h-[40px]",
      "!border-0",
      "!shadow-none",
    ),
  container: () => clsx("!bg-transparent", "!text-blue-500", "w-full"),
  option: (props: any) =>
    clsx(
      "dark:text-white",
      "!cursor-pointer",
      props.isSelected
        ? "!text-white dark:!text-white font-medium dark:!bg-purple-400 !bg-emerald-400"
        : "!bg-transparent",
    ),
  input: () => clsx("dark:!text-white"),
  menu: () =>
    clsx(
      "dark:!bg-black",
      "max-h-[240px]",
      "overflow-y-auto",
      "scrollbar-thin",
      "touch-auto",
      "pointer-events-auto",
      "text-xs",
    ),
  singleValue: () => clsx("dark:!text-white"),
  placeholder: () =>
    clsx({
      "!text-gray-500 dark:!text-gray-300 !text-xs md:!text-sm truncate": true,
    }),
};

const sharedClassName = clsx(
  "border-solid",
  "border-b-2",
  "text-xs",
  "md:text-sm",
  // "rounded",
  "leading-none",
  "h-[40px] md:h-[40px]",
  "font-light",
  "placeholder:text-xs",
  "md:placeholder:text-sm",
  "placeholder:font-light",
  "bg-transparent",
  "dark:text-white",
);

const formatOptionLabel = (option: ICustomSelectOption) => (
  <div
    style={{
      display: "flex",
      alignItems: "center",
      gap: 8,
    }}
  >
    {option.icon && (
      <>
        {option.iconType === ECustomSelectIconType.ICON && (
          <Icon
            name={option.icon as any}
            size={ICON_SIZES.SM}
            {...(option.iconStyles && {
              style: option.iconStyles,
            })}
            className={clsx(["rounded", "p-1"])}
          />
        )}
        {option.iconType === ECustomSelectIconType.CURRENCY && (
          <div
            style={{
              height: ICON_SIZES.SM,
              width: ICON_SIZES.SM,
              ...(option.iconStyles && option.iconStyles),
            }}
            className={clsx(["rounded", "inline-flex", "!items-center"])}
          >
            <CurrencyFlag currency={option.icon} size="md" />
          </div>
        )}
        {option.iconType === ECustomSelectIconType.COUNTRY && (
          <div
            style={{
              height: ICON_SIZES.SM,
              width: ICON_SIZES.SM,
              ...(option.iconStyles && option.iconStyles),
            }}
            className={clsx(["rounded", "inline-flex", "!items-center"])}
          >
            <ReactCountryFlag
              countryCode={option.icon}
              svg
              style={{
                width: ICON_SIZES.SM,
                height: ICON_SIZES.SM,
              }}
            />
          </div>
        )}
      </>
    )}
    <span>{option.label}</span>
  </div>
);

const formatGroupLabel = (data: any) => (
  <div
    className={clsx([
      "flex",
      "flex-row",
      "justify-between",
      "items-center",
      "text-black",
    ])}
  >
    <span>{data.label}</span>
    <Badge kind={EBadgeKind.INFO} text={data.options.length} />
  </div>
);

const CustomSelect: React.FC<ICustomSelect> = ({
  options = [],
  label,
  placeholder,
  id,
  required,
  value = "",
  onChange,
  disabled = false,
  autoFocus = false,
  form = "",
  creatable = false,
  useValueAsLabel = true,
  isClearable = true,
  isSearchable = true,
  isMulti = false,
  hideSelectedOptions = false,
  closeMenuOnSelect = true,
  allowSearchModal = false,
  isInsideGrid = false,
  helpText,
  withIcons = false,
  searchModalOptions = null,
}) => {
  const selectAutoId = useId();
  const { t } = useTranslation();
  const containerRef = useRef<HTMLDivElement>(null);
  const { isMobile } = useMobile();
  const [showModalSelect, setShowModalSelect] = useState<boolean>(false);

  const flattenedOptions = useMemo(() => {
    return flattenOptions(options);
  }, [options]);

  const selectedValue = useMemo(() => {
    if (isMulti) {
      if (!value) return [];

      if (isOptionArray(value)) {
        return value;
      }

      if (Array.isArray(value)) {
        return value.map((item) => {
          const itemValue = String(item);
          const foundOption = flattenedOptions.find(
            (option) => option.value === itemValue,
          );

          return {
            value: itemValue,
            label: useValueAsLabel
              ? itemValue
              : foundOption?.label || itemValue,
            ...(foundOption?.icon ? { icon: foundOption.icon } : {}),
          };
        });
      }

      return [];
    }

    if (!value) return null;

    if (isOption(value)) {
      return value;
    }

    const stringValue = String(value);
    const foundOption = flattenedOptions.find(
      (option) => option.value === stringValue,
    );

    return {
      value: stringValue,
      label: useValueAsLabel ? stringValue : foundOption?.label || stringValue,
      ...(foundOption?.icon ? { icon: foundOption.icon } : {}),
    };
  }, [value, flattenedOptions, useValueAsLabel, isMulti]);

  const updateValue = useCallback(
    (theNewValue: any) => onChange(theNewValue),
    [onChange],
  );

  const commonProps = {
    components: {
      IndicatorSeparator: () => null,
      ValueContainer: CustomValueContainer,
      MenuList: CustomMenuList,
    },
    styles: sharedStyles,
    classNames: {
      ...sharedClassNames,
      control: () =>
        clsx(sharedClassNames.control(), {
          "!h-[40px] md:!h-[40px] !max-h-[40px]": isInsideGrid,
        }),
    },
    className: clsx(sharedClassName, {
      "border-red-200 dark:border-teal-500": required && !value,
      "border-gray-200 dark:border-gray-600": (required && value) || !required,
      "border-gray-300": !required,
      "placeholder:text-gray-500": !required,
      "placeholder:text-red-500": required,
    }),
    menuPortalTarget: document.body,
    menuShouldBlockScroll: isMobile ? false : true,
    required,
    isSearchable,
    hideSelectedOptions,
    closeMenuOnSelect,
    isClearable,
    menuPlacement: "auto" as const,
    options,
    placeholder,
    onBlur: (e: any) => {
      // if (creatable && !isMobile) {
      //   if (value !== e.target.value) {
      //     updateValue({ value: String(e.target.value).trim() });
      //   }
      // }
    },
    onChange: (e: any) => {
      onChange(e);
    },
    value: selectedValue,
    id: id || selectAutoId,
    isDisabled: disabled,
    isMulti,
    autoFocus,
    openMenuOnClick: true,
    ...(form ? { form } : {}),
  };

  const getMultiValueFirstItem = () => {
    const selected = (selectedValue || []) as any[];

    if (selected.length) {
      const [firstItem]: any = selectedValue;

      return useValueAsLabel ? firstItem?.label : firstItem?.value;
    }

    return "";
  };

  return (
    <>
      <div className="flex flex-col w-full" ref={containerRef}>
        {label && (
          <FieldLabel
            required={required}
            text={label}
            htmlFor={id || selectAutoId}
            valid={!!value}
          />
        )}

        <div className={clsx(["flex", "w-full", "flex-row", "gap-2"])}>
          <div className={clsx(["flex", "w-full", "items-end"])}>
            {creatable ? (
              <CreatableSelect
                {...commonProps}
                formatGroupLabel={formatGroupLabel}
                formatCreateLabel={(inputValue) => (
                  <div
                    className={clsx([
                      "flex",
                      "gap-2",
                      "w-full",
                      "flex-row",
                      "flex-shrink",
                      "truncate",
                      "cursor-pointer",
                    ])}
                  >
                    <div
                      className={clsx([
                        "inline-flex",
                        "items-center",
                        "justify-center",
                      ])}
                    >
                      <MdAdd size={ICON_SIZES.XS} className="shrink-0" />
                      <span className="font-medium md:text-sm text-xs">
                        {t("inputs:select.add_option")}
                      </span>
                    </div>
                    <Badge
                      text={inputValue.trim()}
                      className="dark:!bg-cyan-400 dark:!text-black"
                    />
                  </div>
                )}
                {...(withIcons
                  ? {
                      formatOptionLabel,
                    }
                  : {})}
              />
            ) : (
              <Select
                {...commonProps}
                formatGroupLabel={formatGroupLabel}
                noOptionsMessage={() => t("inputs:select.no_elements")}
                {...(withIcons
                  ? {
                      formatOptionLabel,
                    }
                  : {})}
              />
            )}
          </div>

          {allowSearchModal && (
            <Button
              onClick={() => setShowModalSelect(true)}
              icon={<MdOutlineManageSearch size={ICON_SIZES.SM} />}
              tooltip={t("inputs:select.choose")}
              className={clsx([
                {
                  "!h-[40px] min:!h-[40px] max:!h-[40px] !border-0 !shadow-none !rounded-none":
                    isInsideGrid,
                },
              ])}
              active={showModalSelect}
            />
          )}
        </div>

        {helpText && (
          <p
            className={clsx([
              "block",
              "text-xs",
              "opacity-50",
              "leading-tight",
              "mt-1",
            ])}
          >
            {helpText}
          </p>
        )}
      </div>

      {showModalSelect && (
        <SelectModal
          onClose={() => setShowModalSelect(false)}
          options={flattenedOptions}
          isMulti={isMulti}
          value={
            isMulti ? ((selectedValue || []) as any[]) : (selectedValue as any)
          }
          onSelect={(value: any) => {
            updateValue(isMulti ? value : { value });
            setShowModalSelect(false);
          }}
          allowCreateOption={creatable}
          title={searchModalOptions?.title}
          description={searchModalOptions?.description}
          defaultSearchValue={
            isMulti
              ? getMultiValueFirstItem()
              : useValueAsLabel
                ? (selectedValue as ICustomSelectOption | null)?.label
                : (selectedValue as ICustomSelectOption | null)?.value
          }
        />
      )}
    </>
  );
};

export default CustomSelect;
